import { useEffect, useRef, useState } from 'react';
import {
  formatDuration,
  playbackFraction,
  playbackPosition,
  type GameMatchView,
} from '@srcbattle/shared';
import { Button } from './ui.tsx';
import { YouTubePlayer, type YouTubePlayerHandle } from './YouTubePlayer.tsx';
import { SpotifyPlayer } from './SpotifyPlayer.tsx';

/**
 * What is on screen while an entry plays.
 *
 * The operator gets a real media player. Everyone else gets the active song's
 * artwork and a progress bar driven by the same server anchor, because they
 * are hearing the audio over Discord — so the room still sees what is playing
 * and where it is in the clip without eight browsers each fetching the media
 * and each producing their own audio or advert.
 *
 * The operator's player is mounted for the whole match, not just while
 * something is playing. Creating it on the first press meant fetching YouTube's
 * iframe API before the video could start, which took long enough that the
 * browser no longer counted the press as a user gesture and refused to play the
 * audio — the first song of the night loaded and then sat there. Keeping it
 * mounted also means the operator can see what is cued up next.
 */
export function MatchStage({
  match,
  serverNow,
  isOperator,
  hostOnly,
  onPlay,
  onResume,
  onSkip,
  onOpenVoting,
  onStarted,
  onPosition,
  onFallbackToYouTube,
  busy,
  layout = 'legacy',
}: {
  match: GameMatchView;
  serverNow: () => number;
  isOperator: boolean;
  hostOnly: boolean;
  onPlay: (slot: 'a' | 'b') => void;
  onResume: () => void;
  onSkip: () => void;
  onOpenVoting: () => void;
  onStarted: (positionMs: number) => void;
  onPosition: (positionMs: number, isPlaying: boolean) => void;
  /** Switches a Spotify entry the room cannot hear onto a matched YouTube video. */
  onFallbackToYouTube: (entryId: string) => void;
  busy: boolean;
  layout?: 'legacy' | 'arena' | 'spotlight' | 'mixtape';
}) {
  const playback = match.playback;
  const [, forceTick] = useState(0);
  const playerRefs = {
    a: useRef<YouTubePlayerHandle>(null),
    b: useRef<YouTubePlayerHandle>(null),
  };
  const [previewOnly, setPreviewOnly] = useState<'a' | 'b' | null>(null);
  const [spotifyUnavailable, setSpotifyUnavailable] = useState<'a' | 'b' | null>(null);

  /**
   * Spotify stopping on its own, early.
   *
   * The symptom people actually report is "it plays for about nine seconds and
   * then stops" — which is Spotify's DRM module failing to renew the licence,
   * not anything this app did. The player reports a stop it was never asked
   * for; if that happens in the first half-minute of a clip, something is
   * wrong with Spotify rather than with the person operating it, and saying so
   * is much kinder than leaving them clicking play again.
   */
  const [spotifyTrouble, setSpotifyTrouble] = useState(false);
  const [alertsHidden, setAlertsHidden] = useState(() => localStorage.getItem('songbattle:hide-player-alerts') === 'yes');
  const startedAtRef = useRef<number | null>(null);

  // The progress bar is derived from the server anchor and the local clock, so
  // it has to be re-rendered on a timer rather than waiting for a socket event.
  const running = Boolean(playback?.isPlaying && !playback.pending);
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => forceTick((n) => n + 1), 250);
    return () => clearInterval(timer);
  }, [running]);

  const nowPlaying =
    playback?.slot === 'a' ? match.a : playback?.slot === 'b' ? match.b : null;

  // What the idle player should have cued: whatever is playing, otherwise the
  // side that has not been heard yet.
  const cued = nowPlaying ?? (match.playedA ? (match.b ?? match.a) : (match.a ?? match.b));

  const fraction = playback ? playbackFraction(playback, serverNow()) : 0;
  const position = playback ? playbackPosition(playback, serverNow()) : 0;
  const elapsed = playback ? position - playback.clipStartMs : 0;
  const clipLength = playback ? playback.clipEndMs - playback.clipStartMs : 0;
  const starting = Boolean(playback?.isPlaying && playback.pending);
  const cuedIsSpotify = cued?.source === 'spotify' && Boolean(cued.spotifyTrackId);

  // The verdict belongs to a track, not to the stage.
  useEffect(() => {
    setPreviewOnly(null);
    setSpotifyUnavailable(null);
  }, [match.a?.id, match.b?.id]);

  /**
   * Which player an entry needs, decided by the entry rather than the lobby.
   *
   * A YouTube entry and a Spotify entry sit in the same bracket and play
   * against each other, so there is no lobby-wide "source" to switch on — each
   * one plays from where it came from. Only the host is affected: everyone else
   * hears whichever it is over Discord and never touches a Spotify account.
   */
  const useSpotifyFor = (entry: { source: string; spotifyTrackId: string | null } | null) =>
    entry?.source === 'spotify' && Boolean(entry.spotifyTrackId);

  /** The id the chosen player needs. Never null for a real entry. */
  const mediaIdOf = (entry: { source: string; spotifyTrackId: string | null; youtubeVideoId: string | null }) =>
    useSpotifyFor(entry) ? (entry.spotifyTrackId as string) : (entry.youtubeVideoId ?? '');

  /**
   * Starts the clip in the click handler.
   *
   * The player call has to happen synchronously here, before the request to the
   * server. Waiting for the response and reacting to the new state puts the
   * play call outside the gesture, and the browser blocks audio that does not
   * come from one.
   */
  const startSlot = (slot: 'a' | 'b') => {
    const entry = slot === 'a' ? match.a : match.b;
    if (entry) {
      playerRefs[slot].current?.playNow(mediaIdOf(entry), entry.clipStartMs / 1000);
    }
    onPlay(slot);
  };

  const resume = () => {
    if (playback) playerRefs[playback.slot].current?.resumeNow();
    onResume();
  };

  return (
    <div className={`match-stage match-stage-${layout} relative flex flex-col gap-3`}>
      {/* Purely decorative furniture, one piece per layout. Kept as real
          elements rather than pseudo-elements so each layout can position them
          against the stage rather than against whatever box happens to be
          underneath. */}
      {layout === 'arena' && (
        <div className="arena-versus pointer-events-none absolute left-1/2 top-32 z-10 hidden -translate-x-1/2 rounded-full border-4 border-surface bg-accent px-4 py-3 text-lg font-black text-accent-fg shadow-xl lg:block">VS</div>
      )}
      {layout === 'spotlight' && <div aria-hidden className="spotlight-beam pointer-events-none" />}
      {layout === 'mixtape' && (
        <div aria-hidden className="mixtape-deck pointer-events-none">
          <span className="mixtape-reel" />
          <span className="mixtape-tape" />
          <span className="mixtape-reel" />
        </div>
      )}
      {isOperator && (match.a || match.b) ? (
        <div className="stage-decks grid grid-cols-1 gap-3 lg:grid-cols-2">
          {(['a', 'b'] as const).map((slot) => {
            const entry = slot === 'a' ? match.a : match.b;
            if (!entry) return null;
            const active = playback?.slot === slot;
            return (
              <section key={entry.id} className="flex min-w-0 flex-col gap-2" aria-label={`Player ${slot.toUpperCase()}`}>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className={`rounded px-1.5 py-0.5 text-xs ${active ? 'bg-accent/15 text-accent' : 'bg-surface-raised text-text-muted'}`}>{slot.toUpperCase()}</span>
                  <span className="truncate">{entry.title}</span>
                </div>
                {useSpotifyFor(entry) ? (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="w-full">
                      <SpotifyPlayer
                        ref={playerRefs[slot]}
                        trackId={entry.spotifyTrackId as string}
                        startSeconds={(active ? (playback?.clipStartMs ?? entry.clipStartMs) : entry.clipStartMs) / 1000}
                        shouldPlay={Boolean(active && playback?.isPlaying)}
                        awaitingStart={Boolean(active && starting)}
                        onStarted={(positionMs) => {
                          if (!active) return;
                          startedAtRef.current = Date.now();
                          setSpotifyTrouble(false);
                          onStarted(positionMs);
                        }}
                        onPosition={(positionMs, isPlaying) => {
                          if (!active) return;
                          const startedAt = startedAtRef.current;
                          if (!isPlaying && playback?.isPlaying && startedAt !== null && Date.now() - startedAt < 30_000) setSpotifyTrouble(true);
                          onPosition(positionMs, isPlaying);
                        }}
                        onPreviewDetected={(detected) => setPreviewOnly(detected ? slot : null)}
                        onUnavailable={() => setSpotifyUnavailable(slot)}
                        onUserPlay={() => {
                          if (!active) onPlay(slot);
                        }}
                        artworkUrl={entry.thumbnailUrl}
                      />
                    </div>
                  </div>
                ) : (
                  <YouTubePlayer
                    ref={playerRefs[slot]}
                    videoId={entry.youtubeVideoId ?? ''}
                    startSeconds={(active ? (playback?.clipStartMs ?? entry.clipStartMs) : entry.clipStartMs) / 1000}
                    shouldPlay={Boolean(active && playback?.isPlaying)}
                    targetPositionMs={active && running ? position : null}
                    awaitingStart={Boolean(active && starting)}
                    onStarted={(positionMs) => active && onStarted(positionMs)}
                    onPosition={(positionMs, isPlaying) => active && onPosition(positionMs, isPlaying)}
                  />
                )}
              </section>
            );
          })}
        </div>
      ) : nowPlaying ? (
        <div className="stage-nowplaying relative overflow-hidden rounded-xl border border-border bg-surface-raised">
          {nowPlaying.thumbnailUrl && (
            <img
              src={nowPlaying.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-15 blur-2xl"
            />
          )}
          <div className="relative flex flex-col items-center gap-4 px-4 py-6 text-center sm:flex-row sm:px-6 sm:text-left">
            {nowPlaying.thumbnailUrl ? (
              <img
                src={nowPlaying.thumbnailUrl}
                alt={`Artwork for ${nowPlaying.title}`}
                className="stage-artwork aspect-square w-full max-w-56 shrink-0 rounded-xl bg-surface object-cover shadow-xl sm:w-44"
              />
            ) : (
              <div
                className="stage-artwork flex aspect-square w-full max-w-56 shrink-0 items-center justify-center rounded-xl bg-surface text-6xl text-text-faint shadow-xl sm:w-44"
                aria-label="No artwork available"
              >
                ♪
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-accent">
                {starting ? 'Starting…' : playback?.isPlaying ? 'Playing now' : 'Paused'}
              </p>
              <h2 className="mt-1 text-balance text-xl font-semibold tracking-tight sm:text-2xl">
                {nowPlaying.title}
              </h2>
              {nowPlaying.artist && (
                <p className="mt-1 text-sm text-text-muted">{nowPlaying.artist}</p>
              )}
              <p className="mt-3 text-xs text-text-faint">
                {hostOnly
                  ? 'Audio is coming through Discord.'
                  : 'The host is playing this one.'}
              </p>
              {starting && nowPlaying.source === 'spotify' && (
                <p className="mt-1 text-xs text-text-faint">Spotify needs one press from the host.</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="stage-idle rounded-xl border border-dashed border-border px-4 py-10 text-center">
          <p className="text-sm text-text-muted">
            {match.playedA && match.playedB
              ? 'Both played. Time to vote.'
              : 'Waiting for the host.'}
          </p>
        </div>
      )}

      {nowPlaying && playback && (
        <div>
          <div className="mb-1 flex items-baseline justify-between gap-3">
            <p className="truncate text-sm font-medium">
              <span className="mr-2 rounded bg-accent/15 px-1.5 py-0.5 text-xs text-accent">
                {playback.slot.toUpperCase()}
              </span>
              {nowPlaying.title}
            </p>
            <span className="shrink-0 font-mono text-xs text-text-faint">
              {/* Holds at 0:00 until the host's player reports it really
                  started, so the timer never runs over silence. */}
              {starting ? 'starting…' : `${formatDuration(elapsed)} / ${formatDuration(clipLength)}`}
            </span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={Math.round(fraction * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Clip progress"
            className="h-2 overflow-hidden rounded-full bg-surface-raised"
          >
            <div
              className="h-full rounded-full bg-accent"
              style={{ width: `${fraction * 100}%` }}
            />
          </div>
        </div>
      )}

      {isOperator && !alertsHidden && cuedIsSpotify && starting && (
        <div className="player-alert rounded-lg border border-accent/40 bg-accent/10 px-2.5 py-1.5 text-xs">
          <strong>Press ▶ in the Spotify player.</strong> The clock starts when you do.
        </div>
      )}

      {isOperator && !alertsHidden && cuedIsSpotify && (previewOnly === playback?.slot || spotifyTrouble || spotifyUnavailable === playback?.slot) && (
        <div className="player-alert flex flex-col gap-2 rounded-lg border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-xs">
          {spotifyUnavailable === playback?.slot ? (
            <p><strong>The Spotify player did not load.</strong> Try the Spotify link above, or switch this entry to YouTube.</p>
          ) : previewOnly === playback?.slot ? (
            <p>
              <strong>That is the 30-second preview.</strong> Press ▶ on the player itself. If it
              stays a preview, sign in to Spotify in this browser.
            </p>
          ) : (
            <p>
              <strong>Spotify stopped on its own.</strong> Open <code>chrome://components</code>,
              update <strong>Widevine Content Decryption Module</strong>, and restart the browser.
            </p>
          )}
          {cued && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => onFallbackToYouTube(cued.id)}
              >
                Play it on YouTube instead
              </Button>
            </div>
          )}
          <Button variant="ghost" onClick={() => { localStorage.setItem('songbattle:hide-player-alerts', 'yes'); setAlertsHidden(true); }}>Hide player alerts</Button>
        </div>
      )}

      {isOperator && !nowPlaying && !match.playedA && !match.playedB && (
        <p className="text-sm text-text-muted">
          {cuedIsSpotify
            ? 'Press play when everyone is ready, then ▶ in the Spotify player.'
            : 'Press play when everyone is ready.'}
        </p>
      )}

      {isOperator && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={match.playedA ? 'secondary' : 'primary'}
            onClick={() => startSlot('a')}
            disabled={busy || !match.a}
          >
            {match.playedA ? 'Replay A' : 'Play A'}
          </Button>
          <Button
            variant={match.playedB ? 'secondary' : 'primary'}
            onClick={() => startSlot('b')}
            disabled={busy || !match.b}
          >
            {match.playedB ? 'Replay B' : 'Play B'}
          </Button>

          {playback && !playback.isPlaying && (
            <Button
              variant="secondary"
              onClick={resume}
              disabled={busy}
            >
              Resume
            </Button>
          )}

          {playback?.isPlaying && (
            <Button variant="ghost" onClick={onSkip} disabled={busy}>
              Skip ahead
            </Button>
          )}

          {/* After a reload the operator's player comes back cued but silent,
              while the server still has the clip running. Nothing can start
              audio without a press, so this is the press. */}
          {running && nowPlaying && (
            <Button
              variant="ghost"
              onClick={() =>
                playerRefs[playback!.slot].current?.playNow(mediaIdOf(nowPlaying), position / 1000)
              }
              disabled={busy}
            >
              Resync my player
            </Button>
          )}

          {/* Later rounds: the room has already heard both of these, twice.
              Making them sit through the clips again to vote is the wrong
              default, so the ballot can be opened on whatever has played. */}
          {!match.votingOpen && (
            <Button variant="ghost" onClick={onOpenVoting} disabled={busy}>
              Open voting
            </Button>
          )}
        </div>
      )}

    </div>
  );
}
